import { SELECTED_ACCOUNT_CHAIN_ID } from '@hoodmorning/contracts';
import { decodeFunctionData, keccak256, parseAbi, type Address, type Hex } from 'viem';
import { z } from 'zod';
import { createCanonicalBlockReadClient, type CanonicalReadPort } from './canonicalBlockReadClient.js';
import { experimentalExternalCoexistenceModuleId } from './externalFixedRouteOrderAbi.js';
import { prepareExternalExecutorSetup, prepareSelectedExternalExecutorSetup, type ExternalExecutorSetupInput, type ExternalExecutorSetupPlan } from './externalExecutorSetup.js';
import { readExternalExecutorTopologyAtBlock, readSelectedExternalExecutorTopologyAtBlock, type ExternalExecutorTopology } from './externalExecutorTopology.js';

const addressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/)
  .refine(value => !/^0x0{40}$/i.test(value))
  .transform(value => value.toLowerCase() as Address);
const hashSchema = z.string().regex(/^0x[0-9a-fA-F]{64}$/)
  .refine(value => !/^0x0{64}$/i.test(value))
  .transform(value => value.toLowerCase() as Hex);
const sourceSchema = z.strictObject({
  number: z.bigint().min(0n).max((1n << 256n) - 1n),
  hash: z.string().regex(/^0x[0-9a-fA-F]{64}$/).transform(value => value.toLowerCase() as Hex),
  timestamp: z.bigint().min(0n).max((1n << 256n) - 1n),
});
const pinSchema = z.strictObject({ account: addressSchema, codeHash: hashSchema });
const profileSchema = z.strictObject({
  schemaVersion: z.literal(1), moduleVersion: z.literal(experimentalExternalCoexistenceModuleId),
  chainId: z.literal(4663), vault: addressSchema, owner: addressSchema, keeper: addressSchema,
  engine: pinSchema, reader: pinSchema, callBuilder: pinSchema,
});

export type ExternalExecutorSetupObservationProfile<ChainId extends 4663 | 46630 = 4663> =
  Omit<z.output<typeof profileSchema>, 'chainId'> & { chainId: ChainId };
export type SelectedExternalExecutorSetupObservationProfile = ExternalExecutorSetupObservationProfile<typeof SELECTED_ACCOUNT_CHAIN_ID>;

export const externalExecutorSetupObservationAbi = parseAbi([
  'function vault() view returns(address)', 'function keeper() view returns(address)',
  'function reader() view returns(address)', 'function callBuilder() view returns(address)',
  'function authoritySelectors() pure returns(bytes4[6])',
  'function globalEpoch() view returns(uint256)', 'function epoch() view returns(uint256)',
  'function armed() view returns(bool)', 'function healthy() view returns(bool)',
  'function nextOrder() view returns(uint256)', 'function moduleId() pure returns(string)',
  'function getExecutionData(bytes4 selector) view returns((address module,bool skipRuntimeValidation,bool allowGlobalValidation,bytes25[] executionHooks))',
  'function getValidationData(bytes24 entity) view returns((uint8 validationFlags,bytes25[] validationHooks,bytes25[] executionHooks,bytes4[] selectors))',
  'function getFallbackSignerData() view returns(address,bool)',
]);
const manifestAbi = parseAbi([
  'function installExecution(address module,((bytes4 executionSelector,bool skipRuntimeValidation,bool allowGlobalValidation)[] executionFunctions,(bytes4 executionSelector,uint32 entityId,bool isPreHook,bool isPostHook)[] executionHooks,bytes4[] interfaceIds) manifest,bytes data)',
]);
const same = (left: string, right: string) => left.toLowerCase() === right.toLowerCase();
const safeObservationFailures = new Set([
  'Canonical block observation failed',
  'External setup profile role collision',
  'External setup engine runtime mismatch',
  'External setup reader runtime mismatch',
  'External setup call builder runtime mismatch',
  'External setup engine identity mismatch',
  'External setup topology source or owner mismatch',
  'External topology module mismatch',
]);

function executorSetupObserver<const ChainId extends 4663 | 46630>(chainId: ChainId,
  prepareExternalExecutorSetup: (input: ExternalExecutorSetupInput<ChainId>) => ExternalExecutorSetupPlan<ChainId>,
  readExternalExecutorTopologyAtBlock: (input: { client: Parameters<typeof readSelectedExternalExecutorTopologyAtBlock>[0]['client'];
    setup: ExternalExecutorSetupInput<ChainId>; blockNumber: bigint }) => Promise<ExternalExecutorTopology<ChainId>>) {
  const domainSchema = profileSchema.extend({ chainId: z.literal(chainId) });
  /** Structural validation, not trust in the supplied deployment/profile pins. */
  function readExternalExecutorSetupObservationProfile(input: ExternalExecutorSetupObservationProfile<ChainId>) {
    const profile = domainSchema.parse(structuredClone(input));
    const roles = [profile.vault, profile.engine.account, profile.reader.account, profile.callBuilder.account];
    if (new Set(roles).size !== roles.length) throw new Error('External setup profile role collision');
    // Constructor/profile authority does not exclude a shared owner/keeper EOA.
    return profile;
  }

  /** Canonical read-only facts before any route configuration. The supplied pins
   * must be independently trusted. Engine/helper identity and current owner are
   * checked, but vault provenance/runtime, validation storage ownership and
   * deployment-inclusive installation history remain unverified. In particular,
   * epoch zero and empty getters never prove absence or select a setup action.
   * Port duration/response-byte bounds remain the caller's responsibility.
   */
  async function observeExternalExecutorSetup(input: {
    port: CanonicalReadPort;
    profile: ExternalExecutorSetupObservationProfile<ChainId>;
    /** Continue a caller-selected, exact canonical source; otherwise observe latest. */
    source?: { number: bigint; hash: Hex; timestamp: bigint };
    policy: { maxRequests: number; maxAgeSeconds: number; maxFutureSeconds: number };
    now?: () => number;
  }) {
    try {
      // Capture every caller-owned input and capability before the first await.
      const profile = readExternalExecutorSetupObservationProfile(input.profile);
      const port = { request: input.port.request.bind(input.port) };
      const requestedSource = input.source === undefined ? undefined : sourceSchema.parse(structuredClone(input.source));
      const policy = structuredClone(input.policy), now = input.now;
      const setupInput = { version: 'external-fixed-route-order.1' as const,
        moduleVersion: profile.moduleVersion, chainId: profile.chainId,
        vault: profile.vault, engine: profile.engine.account };
      const setup = prepareExternalExecutorSetup(setupInput);
      const manifest = decodeFunctionData({ abi: manifestAbi, data: setup.steps[0].call.data });
      const expectedSelectors = manifest.args[1].executionHooks.map(hook => hook.executionSelector);
      const snapshot = await createCanonicalBlockReadClient({ port, source: requestedSource, policy, now });
      const { client, source } = snapshot, blockNumber = source.number;
      await Promise.all(([['engine', profile.engine], ['reader', profile.reader],
        ['call builder', profile.callBuilder]] as const).map(async ([role, pin]) => {
        const code = await client.getBytecode({ address: pin.account, blockNumber });
        if (!code || code === '0x' || !same(keccak256(code), pin.codeHash)) {
          throw new Error(`External setup ${role} runtime mismatch`);
        }
      }));
      const [vault, keeper, reader, callBuilder, selectors] = await Promise.all([
        client.readContract({ address: profile.engine.account, abi: externalExecutorSetupObservationAbi,
          functionName: 'vault', blockNumber }),
        client.readContract({ address: profile.engine.account, abi: externalExecutorSetupObservationAbi,
          functionName: 'keeper', blockNumber }),
        client.readContract({ address: profile.engine.account, abi: externalExecutorSetupObservationAbi,
          functionName: 'reader', blockNumber }),
        client.readContract({ address: profile.engine.account, abi: externalExecutorSetupObservationAbi,
          functionName: 'callBuilder', blockNumber }),
        client.readContract({ address: profile.engine.account, abi: externalExecutorSetupObservationAbi,
          functionName: 'authoritySelectors', blockNumber }),
      ]);
      if (!same(vault, profile.vault) || !same(keeper, profile.keeper)
        || !same(reader, profile.reader.account) || !same(callBuilder, profile.callBuilder.account)
        || selectors.length !== expectedSelectors.length
        || selectors.some((selector, index) => !same(selector, expectedSelectors[index]!))) {
        throw new Error('External setup engine identity mismatch');
      }
      const topology = await readExternalExecutorTopologyAtBlock({ client, setup: setupInput, blockNumber });
      if (topology.sourceBlockNumber !== blockNumber || !same(topology.vault, profile.vault)
        || !same(topology.engine, profile.engine.account) || topology.moduleVersion !== profile.moduleVersion
        || !same(topology.owner, profile.owner) || topology.ownerDisabled) {
        throw new Error('External setup topology source or owner mismatch');
      }
      await snapshot.verifySource();
      return { kind: 'external-executor-setup-observation' as const, profile, topology,
        sourceBlockNumber: source.number, sourceBlockHash: source.hash, sourceBlockTimestamp: source.timestamp,
        currentOwner: topology.owner.toLowerCase() as Address, ownerDisabled: topology.ownerDisabled,
        globalEpoch: topology.globalEpoch, epoch: topology.globalEpoch, armed: topology.armed,
        healthy: topology.healthy, nextOrder: topology.nextOrder,
        canonicalSourceVerified: true as const, routeConfigurationObserved: false as const,
        profileApproved: false as const, profileTrustVerified: false as const,
        vaultProvenanceVerified: false as const, vaultRuntimeVerified: false as const,
        vaultOwnershipVerified: false as const, validationStorageOwnerVerified: false as const,
        deploymentInclusiveHistoryVerified: false as const, absenceProven: false as const,
        modulesVerified: false as const, coexistenceVerified: false as const,
        setupAuthorized: false as const, executionAuthorized: false as const,
        signingAuthorized: false as const, submissionAuthorized: false as const, finalityProven: false as const,
        verification: 'trusted-profile-vault-provenance-and-installation-history-required-before-owner-review' as const };
    } catch (cause) {
      // Preserve only a fixed, reviewed reason for server-side diagnosis.
      // Raw provider errors may contain endpoint credentials or calldata.
      const reason = cause instanceof Error && safeObservationFailures.has(cause.message)
        ? cause.message : 'unclassified';
      throw new Error('External executor setup observation failed', { cause: new Error(reason) });
    }
  }
  return { readExternalExecutorSetupObservationProfile, observeExternalExecutorSetup };
}

export const { readExternalExecutorSetupObservationProfile, observeExternalExecutorSetup } =
  executorSetupObserver<4663>(4663, prepareExternalExecutorSetup, readExternalExecutorTopologyAtBlock);
const selectedSetupObserver = executorSetupObserver(SELECTED_ACCOUNT_CHAIN_ID,
  prepareSelectedExternalExecutorSetup, readSelectedExternalExecutorTopologyAtBlock);
export const readSelectedExternalExecutorSetupObservationProfile = selectedSetupObserver.readExternalExecutorSetupObservationProfile;
export const observeSelectedExternalExecutorSetup = selectedSetupObserver.observeExternalExecutorSetup;
export type ExternalExecutorSetupObservation<ChainId extends 4663 | 46630 = 4663> = Awaited<ReturnType<ReturnType<typeof executorSetupObserver<ChainId>>['observeExternalExecutorSetup']>>;
export type SelectedExternalExecutorSetupObservation = ExternalExecutorSetupObservation<typeof SELECTED_ACCOUNT_CHAIN_ID>;
